import * as React from 'react';
import './AppointmentForm.scss';
import styled from 'styled-components';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { AppointmentData, AppointmentChangeReason, BulkAppointmentCreationResult, ClientPack, ClientRequestType, ExtraItem, FeatureEnum, GeneralSettingsData, GroupAppointmentClient, Invoice, InvoiceDocumentType, InvoiceState, Payment, PermissionEnum, ProfessionalData, RecurrenceFrequency, ServiceType, InvoiceReduced, AppointmentChangeOrigin, CommunicationType, SurveyHistoryItem } from '../../../models';
import { Spinner, Form, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { AutocompleteTextField, CheckInputField, NumberInputField, TextInputField, TimeDayIntervalField, RecurringEventField, MultiplePaymentsField } from '../fields';
import { FormHelperText, Tooltip } from '@mui/material';
import { ClientPackEntity } from '../../../store/entities/state';
import ColorBall from '../../ColorBall';
import AppIconButton from '../../AppIconButton';
import Delete from '@material-ui/icons/Delete';
import { useExpensesAvailable, usePackSettings, useServicesSettings, useRoomsSet, useGeneralSettings } from '../../../store/settings/hooks';
import { ChangeLog } from '../../../models';
import ChangeLogInfo from '../../ChangeLogInfo';
import ExtrasList from '../ExtrasList';
import LaunchIcon from '@material-ui/icons/Launch';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import ReceiptIcon from '@material-ui/icons/Receipt';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import WarningIcon from '@material-ui/icons/Warning';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import AssignmentIcon from '@material-ui/icons/Assignment';
import { TimeDayInterval } from '../fields/TimeDayIntervalField';
import Typography from '../../Typography';
import { Checkbox, IconButton, Menu, MenuItem } from '@material-ui/core';
import { daysBetween, isGuid, minutesToTimeString, parseTimeStringToMinutes, toDateOnlyString } from '../../../shared/utils';
import CustomDayCalendar, { CalendarSchedule } from '../../CustomDayCalendar';
import { getDateDisplayString } from '../../../pages/Home/HomeCalendar/Home';
import { useDispatch, useSelector } from 'react-redux';
import { confirmClientAppointment, getAppointmentAsync, getAppointmentsAsync, getClientAsync, getClientPackAsync, getClientPacksAsync, getWaitingListAsync, getWaitingListByProfessionalAsync, sendClientCommunicationAsync, updateAppointmentConfirmationStatusAsync } from '../../../ajax/requests';
import { alertError, alertSuccess } from '../../../store/alerts/actions';
import { MultipleDataCheckboxesProps } from '../fields/MultipleDataCheckboxes';
import { useEntity } from '../../../store/entities/hooks';
import { entityCreating, loadEntities, updateEntity } from '../../../store/entities/actions';
import { getActualPriceWithPack, getAppointmentTotalPayments, getTotalPrice, serviceName } from '../../../shared/appointmint/appointment';
import { canEditOwnClients, canSeeAppointments as canSeeAppointmentsSelector, canSeeInvoices, getPermissions, hasFeature, isAdministrator } from '../../../store/session/selectors';
import { hide } from '../../../shared/style';
import { InvoicesTable } from '../../../pages/Clients/ClientInvoicesTable';
import moment from 'moment';
import 'moment/locale/fr';
import 'moment/locale/pt';
import ToolTip from '../../ToolTip';
import { getSelectedBranch } from '../../../store/general/selectors';
import { updateGeneralSettings } from '../../../store/settings/actions';
import { PaymentsList } from '../fields/PaymentsList';
import { AvailablePackOption, getPackDesc, PaymentsButtons } from '../fields/PaymentsButtons';
import { useTaxData } from '../../../hooks/useTaxData';
import { ApplicationState } from '../../../store';
import { formatEuros } from '../../../shared/string';
import { getErrorMessage } from '../../../ajax/errors';
import { handleInvoiceCreatedWithSync, handleReceiptEmittedWithSync } from './invoiceSync';
import { buildAppointmentDayCalendarSchedules } from './dayCalendarSchedules';
import {
    createClientSearchConfig,
} from '../../../shared/clientSearch';
import { isAppointmentRoomRequired } from './appointmentRoomPolicy';
import { SendSurveyModal, SurveySentContext, useSendSurveyAction } from '../../Surveys/useSendSurveyAction';
import { buildReplacementAppointmentDraft, createDefaultAppointmentChangeReason, hasAppointmentDeletionBlockers } from '../../../shared/appointmint/appointmentDeletion';
import { useAppointmentDeletionFlow } from '../../Appointments/AppointmentDeletionFlow';
import EditIcon from '@material-ui/icons/Edit';
import { applyAppointmentConfirmationStatus } from '../../../shared/appointmint/appointmentConfirmationStatus';
import AppointmentConfirmationStatusMenu from '../../Appointments/AppointmentConfirmationStatusMenu';
import AppButton from '../../AppButton';
import { buildAppointmentTypeDisplayOptions } from './appointmentTypeOptions';

export interface Client {
    id: string,
    name: string,
    origin?: string,
    phoneNumbers?: string[],
    email?: string,
    clientFileNumber?: number,
    warningNote?: string,
}

export type Professional = Pick<ProfessionalData, 'id' | 'name' | 'capabilities' | 'colorCode' | 'branchTimeAvailability' | 'timeAvailability' | 'absences' | 'availabilityExtra' | 'branchAbsences' | 'branchAvailabilityExtra'>

export interface AppointmentFormStateProps {
    original?: AppointmentData,
    data: AppointmentData,
    userProfessionalId?: string,
    clientPacks?: ClientPackEntity[],
    professionalPacks?: ClientPackEntity[],
    hasChanged: boolean,
    clients: Client[],
    professionals: Professional[],
    isSubmitting?: boolean,
    type: 'edit' | 'create',
    canManageAppointments: boolean,
    canManageClients: boolean,
    canSeePrices: boolean,
    canEditPayments: boolean,
    canDeletePayments: boolean,
    changeLog?: ChangeLog[],
    openPaymentModal?: boolean,
    createInvoice?: boolean,
}

export interface AppointmentFormDispatchProps {
    onChange: (data: AppointmentData) => void,
    onLoad?: () => void,
    onSave: (data: AppointmentData, changeReason: AppointmentChangeReason | undefined, ignoreAvailability: boolean, updateEntireGroup?: boolean, keepOpen?: boolean) => Promise<unknown>,
    onCreateMany?: (data: AppointmentData[], ignoreAvailability: boolean) => Promise<BulkAppointmentCreationResult|null>
    onCreateNewClient?: (clientName: string, professionalId?: string) => Promise<string | undefined>
    onCancel?: () => void,
    onDelete?: (deleteEntireGroup?: boolean, reason?: AppointmentChangeReason) => void,
    onOpenClient?: (clientId: string) => void,
    onOpenProfessional?: (professionalId: string) => void,
    onLoadClientPacks?: (clientId: string) => void,
    onLoadProfessionalPacks?: (professionalId: string) => void,
}

export type AppointmentFormProps = AppointmentFormStateProps & AppointmentFormDispatchProps;

const GroupClientsSection: React.FC<{
    clients: Client[],
    groupClients: GroupAppointmentClient[],
    appointmentType: string,
    disabled: boolean,
    onOpenClient?: (clientId: string) => void,
    onChange: (groupClients: GroupAppointmentClient[]) => void,
}> = ({ clients, groupClients, appointmentType, disabled, onOpenClient, onChange }) => {
    const t = useTranslations();
    const [clientPacksCache, setClientPacksCache] = React.useState<Record<string, ClientPack[]>>({});

    React.useEffect(() => {
        groupClients.forEach(gc => {
            if (!gc.clientId || gc.clientId in clientPacksCache) return;
            setClientPacksCache(prev => ({...prev, [gc.clientId!]: []}));
            getClientPacksAsync(gc.clientId)
                .then(packs => setClientPacksCache(prev => ({...prev, [gc.clientId!]: packs})))
                .catch(() => {});
        });
    }, [groupClients]);

    const hasSubscription = (clientId: string): boolean | undefined => {
        if (!(clientId in clientPacksCache)) return undefined;
        return clientPacksCache[clientId].some(p =>
            p.isSubscription &&
            p.appointmentTypes?.includes(appointmentType) &&
            (!p.validityTo || new Date(p.validityTo) >= new Date())
        );
    };

    const updateClient = (index: number, updated: GroupAppointmentClient) =>
        onChange(groupClients.map((c, i) => i === index ? updated : c));

    const removeClient = (index: number) =>
        onChange(groupClients.filter((_, i) => i !== index));

    const addClient = () =>
        onChange([...groupClients, {}]);

    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)'}}>
            <label style={{fontSize: '0.875rem', fontWeight: 500, marginBottom: 0}}>{t(TK.participants)}</label>
            {groupClients.map((gc, index) => (
                <div key={index} style={{display: 'flex', alignItems: 'center'}}>
                    <AutocompleteTextField
                        style={{flexGrow: 1}}
                        value={gc.clientName ?? ''}
                        label={t(TK.client)}
                        options={clients.map(c => c.name)}
                        disabled={disabled}
                        onValueChanged={name => {
                            const client = clients.find(c => c.name === name);
                            updateClient(index, { ...gc, clientId: client?.id, clientName: name || undefined });
                        }}
                    />
                    {gc.clientId && hasSubscription(gc.clientId) === false && (
                        <Tooltip title={t(TK.noSubscriptionForType)}>
                            <span style={{display: 'flex', alignItems: 'center', marginLeft: 'var(--spacer-XS)'}}>
                                <WarningIcon style={{color: 'var(--warning-color)', fontSize: '1.2rem'}} />
                            </span>
                        </Tooltip>
                    )}
                    {onOpenClient && gc.clientId && (
                        <AppButton className="entityLinkButton" color="link" onClick={() => onOpenClient(gc.clientId!)}>
                            <LaunchIcon/>
                        </AppButton>
                    )}
                    <IconButton
                        style={{
                            color: gc.attended === true
                                ? 'var(--success-color)'
                                : gc.attended === false
                                    ? 'var(--danger-color)'
                                    : 'var(--text-muted)',
                        }}
                        onClick={() => updateClient(index, {
                            ...gc,
                            attended: gc.attended === undefined ? true : gc.attended === true ? false : undefined,
                        })}
                        disabled={disabled}
                    >
                        {gc.attended === false ? <CancelOutlinedIcon /> : <CheckCircleOutlineIcon />}
                    </IconButton>
                    {!disabled && (
                        <AppButton className="entityLinkButton" color="outline" onClick={() => removeClient(index)}>
                            <RemoveIcon/>
                        </AppButton>
                    )}
                </div>
            ))}
            {!disabled && (
                <AppButton color="link" size="sm" style={{alignSelf: 'flex-start', padding: 0}} onClick={addClient}>
                    <AddIcon fontSize="small" style={{marginRight: 'var(--spacer-XXS)'}}/>
                    {t(TK.addClient)}
                </AppButton>
            )}
        </div>
    );
};

export const checkProfessionalCanEditAppointment = (
    data: AppointmentData, 
    professionalId: string|undefined,
    settings: GeneralSettingsData,
): boolean => {
    const appointmentDate = toDateOnlyString(data.date);
    if(!professionalId || data.professionalId !== professionalId || !appointmentDate || !data.start) return false 
    const maxEditDate = new Date(`${appointmentDate} ${data.start}`)
    if (Number.isNaN(maxEditDate.getTime())) return false
    maxEditDate.setMinutes(maxEditDate.getMinutes() + (settings.professionalCanEditAppointmentUpToMinutes ?? 0))
    return new Date < maxEditDate
}

const recurringFields: (keyof AppointmentData)[] = [
    'clientId',
    'professionalId',
    'room',
    'type',
    'date',
    'price',
    'date',
    'start',
    'end',
] 

const buildStartDateTime = (appointment: AppointmentData): Date | null => {
    if (!appointment.date) return null;
    const date = new Date(appointment.date);
    if (Number.isNaN(date.getTime())) return null;

    if (appointment.start) {
        const [hours, minutes] = appointment.start.split(':').map(v => parseInt(v, 10));
        date.setHours(hours || 0, minutes || 0, 0);
    }

    return date;
}

const isFutureAppointment = (appointment: AppointmentData): boolean => {
    const startDate = buildStartDateTime(appointment);
    return startDate ? startDate.getTime() > Date.now() : false;
}

const getConfirmationStatus = (appointment: AppointmentData): 'confirmed' | 'rejected' | 'pending' => {
    if (appointment.confirmedByClientAt) return 'confirmed';
    if (appointment.deniedByClientAt) return 'rejected';
    return 'pending';
}

const getConfirmationRequestSentAt = (appointment: AppointmentData): Date | null => {
    const startDate = buildStartDateTime(appointment);
    if (!startDate) return null;

    const requestHours = [
        ...(appointment.confirmationRequestsSentBySms ?? []),
        ...(appointment.confirmationRequestsSentByWhatsapp ?? []),
    ];
    if (!requestHours.length) return null;

    const latestHoursBefore = Math.min(...requestHours);
    return new Date(startDate.getTime() - latestHoursBefore * 60 * 60 * 1000);
}

const formatDateTime = (value?: string | Date | null): string => {
    if (!value) return '';
    return moment(value).format('DD/MM/YYYY HH:mm');
}

const AppointmentConfirmation = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XXS);
    justify-content: flex-end;
    text-align: end;
    width: 100%;
`;

const PaymentWarningBanner = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
    padding: var(--spacer-XS) var(--spacer-S);
    background-color: var(--warning-bg);
    border: var(--border-thin) solid var(--warning-border);
    border-left: var(--border-thick) solid var(--warning-color);
    border-radius: 4px;
`;

type ConfirmationIndicatorProps = React.HTMLAttributes<HTMLSpanElement> & {
    $status: 'confirmed' | 'rejected' | 'pending';
};

const ConfirmationIndicator = styled.span<ConfirmationIndicatorProps>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${props => (props.$status === 'confirmed'
        ? 'var(--success-color)'
        : props.$status === 'rejected'
            ? 'var(--danger-color)'
            : 'var(--warning-color)')};
`;

const AppointmentForm: React.FC<AppointmentFormProps> = (props) => {

    const {
        type,
        data,
        original,
        hasChanged,
        isSubmitting,
        canManageAppointments,
        canManageClients,
        clients,
        clientPacks,
        professionals,
        professionalPacks,
        changeLog,
        canSeePrices,
        canEditPayments,
        canDeletePayments,
        userProfessionalId,
        openPaymentModal,
        createInvoice,
        onChange,
        onLoad,
        onSave,
        onCreateMany,
        onCancel,
        onDelete,
        onOpenClient,
        onOpenProfessional,
        onLoadClientPacks,
        onLoadProfessionalPacks,
        onCreateNewClient,
    } = props;

    const t = useTranslations();
    const dispatch = useDispatch();

    const taxes = useTaxData();

    const useProfessionalPacks = useSelector(hasFeature(FeatureEnum.ProfessionalPacks))
    const useBranches = useSelector(hasFeature(FeatureEnum.Branches))
    const seeInvoices = useSelector(canSeeInvoices)
    const selectedBranch = useSelector(getSelectedBranch)
    const professionalCanEditOwnClient = useSelector(canEditOwnClients)
    const canSeeAppointments = useSelector(canSeeAppointmentsSelector)
    const canSkipAvailability = useSelector((state: ApplicationState) => !getPermissions(state).includes(PermissionEnum.DoNotAllowSkipAvailability))
    const canConfirmAppointments = useSelector((state: ApplicationState) => !getPermissions(state).includes(PermissionEnum.DoNotAllowToConfirmAppointments))
    const canCreateOrEditAppointments = useSelector((state: ApplicationState) => !getPermissions(state).includes(PermissionEnum.DoNotCreateOrEditAppointments))
    const confirmationFeatureAvailable = useSelector(
        (state: ApplicationState) => (state.settings.reminders?.appointmentConfirmation?.channels?.length ?? 0) > 0
    )
    const canEmitInvoices = useSelector((state: ApplicationState) => seeInvoices && !getPermissions(state).includes(PermissionEnum.DoNotAllowToEmitInvoices))

    const roomsSet = useRoomsSet();
    const generalSettings = useGeneralSettings();
    const servicesSettings = useServicesSettings();
    const expensesAvailable = useExpensesAvailable(data.type)
    const packData = useEntity<ClientPackEntity>('clientPacks', data.packId ?? 'none')
    const packSettings = usePackSettings(packData?.packTemplateId ?? '', packData?.packVersion ?? 0)
    const isAdmin = useSelector(isAdministrator)
    
    const [ignoreAvailability, setIgnoreOverlaps] = React.useState(false);
    const [avoidFirstAutoAssignPack, setAvoidFirstAutoAssignPack] = React.useState(false);
    const [recurringActionModal, setRecurringActionModal] = React.useState<'edit' | null>(null);
    const [invoiceModal, _setInvoiceModal] = React.useState(false);
    const [emitReceiptForInvoiceId, setEmitReceiptForInvoiceId] = React.useState<string | undefined>(undefined);
    const [repeatDates, setRepeatDates] = React.useState<TimeDayInterval[]>([]);
    const [recurring, setRecurring] = React.useState(!!data.recurring);
    const [professionalSlots, _setProfessionalSlots] = React.useState(data.additionalProfessionals?.length ?? 0);
    const [recurringOperation, setRecurringOperation] = React.useState<'creating' | 'deleting' | null>(null);
    const [pendingAction, setPendingAction] = React.useState<'save' | null>(null);
    const [pendingReason, setPendingReason] = React.useState<AppointmentChangeReason | undefined>(undefined);
    const [openExtrasPopup, setOpenExtrasPopup] = React.useState(false);
    const [confirmationMenuAnchor, setConfirmationMenuAnchor] = React.useState<HTMLElement | null>(null);
    const latestDataRef = React.useRef(data);
    const surveyClientEmail = React.useMemo(
        () => clients.find(client => client.id === data.clientId)?.email?.trim() || undefined,
        [clients, data.clientId],
    );
    const handleSurveySent = React.useCallback(async (context?: SurveySentContext) => {
        const optimisticHistory = context ? [
            ...(latestDataRef.current.surveyHistory ?? []),
            {
                id: `sent-${context.surveyId}-${context.sentAt}`,
                surveyId: context.surveyId,
                surveyName: context.surveyName ?? '',
                appointmentId: latestDataRef.current.id,
                sentAt: context.sentAt,
                isSubmitted: false,
                isAnonymousSubmission: false,
                values: {},
                professionalId: latestDataRef.current.professionalId,
                professionalName: latestDataRef.current.professionalName,
                serviceTypeId: latestDataRef.current.type,
                serviceTypeName: latestDataRef.current.typeName,
            } as SurveyHistoryItem,
        ] : latestDataRef.current.surveyHistory;

        if (optimisticHistory) {
            onChange({ ...latestDataRef.current, surveyHistory: optimisticHistory });
        }

        if (type !== 'edit' || !latestDataRef.current.id) {
            return;
        }

        try {
            const updatedAppointment = await getAppointmentAsync(latestDataRef.current.id);
            onChange({
                ...latestDataRef.current,
                surveyHistory: updatedAppointment.surveyHistory ?? optimisticHistory ?? [],
            });
        } catch {
            if (optimisticHistory) {
                onChange({ ...latestDataRef.current, surveyHistory: optimisticHistory });
            }
        }
    }, [onChange, type]);
    const sendSurveyAction = useSendSurveyAction(data.clientId ?? '', type === 'edit' ? data.id : undefined, surveyClientEmail, handleSurveySent);
    const canSendSurveyFromAppointment = type === 'edit' && !!data.clientId && sendSurveyAction.canRenderAction;

    React.useEffect(() => {
        latestDataRef.current = data;
    }, [data]);

    const appointmentDeletion = useAppointmentDeletionFlow({
        appointmentChangeReasons: generalSettings?.appointmentChangeReasons,
        companyName: generalSettings?.companyName,
        executeDelete: (_appointment, deleteEntireGroup, reason) => {
            const showRecurringDeletion = !!deleteEntireGroup;
            return withRecurringAnimation('deleting', showRecurringDeletion, () =>
                Promise.resolve().then(() => onDelete?.(deleteEntireGroup, reason)),
            ).then(() => undefined);
        },
        getAppointmentTypeName: appointment => serviceName(appointment.type, generalSettings),
        loadWaitingList: appointment => getWaitingListAsync(appointment.type, appointment.professionalId).catch(() => []),
        loadWaitingListClient: clientId => getClientAsync(clientId).catch(() => undefined),
        onAddAppointmentChangeReason: newReason => dispatch(updateGeneralSettings({
            ...generalSettings,
            appointmentChangeReasons: [...(generalSettings.appointmentChangeReasons || []), newReason],
        })),
        onCreateReplacementAppointment: (appointment, item) => {
            setTimeout(() => dispatch(entityCreating(
                'appointments',
                buildReplacementAppointmentDraft(appointment, item) as AppointmentData,
            )), 1500);
        },
        onDeleteBlocked: () => dispatch(alertError(t(TK.cannotDeleteAppointmentWithPaymentsOrInvoices))),
        onSmsError: error => dispatch(alertError(getErrorMessage(error) || t(TK.somethingWentWrong))),
        onSmsSent: () => dispatch(alertSuccess(t(TK.sentSuccessfully))),
        sendSms: (clientId, message) => sendClientCommunicationAsync(clientId, CommunicationType.SMS, message).then(() => undefined),
    });

    const refreshClientPacks = () => data.clientId && canEditPayments && onLoadClientPacks && onLoadClientPacks(data.clientId)
    const refreshProfessionalPacks = () => useProfessionalPacks && !professionalPacks?.length && data.professionalId && onLoadProfessionalPacks && onLoadProfessionalPacks(data.professionalId)

    const setInvoiceModal = (value: boolean) => {
        _setInvoiceModal(value)
    }

    const withRecurringAnimation = <T,>(action: 'creating' | 'deleting', enabled: boolean, fn: () => Promise<unknown>) => {
        if (!enabled) return fn();
        setRecurringOperation(action);
        return fn().finally(() => setRecurringOperation(null));
    }

    // eslint-disable-next-line
    React.useEffect(() => { 
        refreshClientPacks()
        refreshProfessionalPacks()        
        onLoad && onLoad()
    }, []);

    React.useEffect(() => {createInvoice && setInvoiceModal(true)}, [createInvoice])

    React.useEffect(() => {
        refreshClientPacks()
        const clientId = data.clientId
        if (clientId) dispatch(loadEntities('clients', () => getClientAsync(clientId).then(e => [e]), 'NewResult'))
    }, [data.clientId])

    React.useEffect(() => {
        refreshProfessionalPacks()
    }, [data.professionalId])

    React.useEffect(() => {
        const packId = data.packId
        if(!packData && packId && isGuid(packId) && data.clientId) {
            const clientId = data.clientId
            dispatch(loadEntities('clientPacks', () => getClientPackAsync(clientId, packId), 'NewResult', [packId]))
        }
    }, [packData, data.packId])

    const reasonOriginMap = {
        [t(TK.professional)]: AppointmentChangeOrigin.Professional,
        [t(TK.client)]: AppointmentChangeOrigin.Client,
        [t(TK.organization)]: AppointmentChangeOrigin.Organization,
        [t(TK.other)]: AppointmentChangeOrigin.Other,
        [AppointmentChangeOrigin.Professional]: t(TK.professional),
        [AppointmentChangeOrigin.Client]: t(TK.client),
        [AppointmentChangeOrigin.Organization]: t(TK.organization),
        [AppointmentChangeOrigin.Other]: t(TK.other),
    }

    const availablePacks = React.useMemo<AvailablePackOption[]>(() => {
        const appointmentDateString = toDateOnlyString(data.date);
        if (!data.start || !data.end || !appointmentDateString) return [];

        const appointmentDate = new Date(appointmentDateString);
        if (Number.isNaN(appointmentDate.getTime())) return [];

        return [...(clientPacks ?? []), ...(professionalPacks ?? [])]
            .filter(pack => {
                const validityFromDateString = toDateOnlyString(pack.validityFrom);
                const validityToDateString = toDateOnlyString(pack.validityTo);

                return (!!pack.totalMinutesStillAvailable || !!pack.totalCreditsStillAvailable || pack.isSubscription)
                    && (!validityFromDateString || daysBetween(appointmentDate, new Date(validityFromDateString)) >= 0)
                    && (!validityToDateString || daysBetween(appointmentDate, new Date(validityToDateString)) <= 0)
            })
            .sort((a, b) => new Date(a.validityFrom ?? 0).getTime() - new Date(b.validityFrom ?? 0).getTime())
            .map(pack => ({
                pack,
                disabled: !!data.type && !pack.appointmentTypes.includes(data.type),
                disabledReason: !!data.type && !pack.appointmentTypes.includes(data.type)
                    ? t(TK.packUnavailableForAppointmentType)
                    : undefined,
            }))
    }, [data.start, data.end, data.type, data.date, clientPacks, professionalPacks, t])

    React.useEffect(() => {
        if (!avoidFirstAutoAssignPack) {
            setAvoidFirstAutoAssignPack(true);
            return;
        }
        const autoAssignPack = !data.packId && !(data.payments?.length ?? 0)
            ? availablePacks.find(option => option.pack.appointmentTypes.includes(data.type))?.pack
            : undefined
        onChange({
            ...data,
            packId: autoAssignPack?.id ?? data.packId,
            packName: autoAssignPack?.packName ?? data.packName,
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.type, data.date, data.clientId, data.professionalId])

    React.useEffect(() => {
        const userProfessional = userProfessionalId && professionals.find(p => p.id === userProfessionalId)
        if(!data.professionalId?.length && userProfessional) {
            onChange({
                ...data,
                professionalName: userProfessional.name,
                professionalId: userProfessional.id,
                professionalColor: userProfessional.colorCode,
            });
        }
    }, [])

    const isEdit = type === 'edit';
    const isCreate = type === 'create';

    const [waitingItems, setWaitingItems] = React.useState<import('../../../models').WaitingListItem[]>([]);
    const [waitingMenuAnchor, setWaitingMenuAnchor] = React.useState<HTMLElement | null>(null);

    const [dayAppointments, setDayAppointments] = React.useState<AppointmentData[]>([]);
    const [calendarDate, setCalendarDate] = React.useState<string | undefined>(undefined);
    const calendarDateOnly = toDateOnlyString(calendarDate ?? data.date);
    const professionalColumnName = calendarDateOnly ? getDateDisplayString(t, new Date(calendarDateOnly)) : '';
    const roomColumnName = data.room || '';

    React.useEffect(() => {
        if (!calendarDateOnly) {
            setDayAppointments([]);
            return;
        }
        const abortController = new AbortController();
        getAppointmentsAsync(calendarDateOnly, calendarDateOnly, abortController.signal)
            .then(appointments => setDayAppointments(appointments))
            .catch(() => {});
        return () => abortController.abort();
    }, [calendarDateOnly]);

    React.useEffect(() => {
        if (isEdit || !data.professionalId || !data.type) {
            setWaitingItems([]);
            return;
        }
        getWaitingListByProfessionalAsync(data.professionalId)
            .then(items => setWaitingItems(items.filter(i => i.service === data.type)))
            .catch(() => setWaitingItems([]));
    }, [isEdit, data.professionalId, data.type]);

    const isProfessional = !!userProfessionalId

    const recurringTooLong = !!data.recurring 
        && !!data.recurring.endDate 
        && !!data.date 
        && moment(data.recurring.endDate, 'YYYY-MM-DD').isAfter(moment(data.date, 'YYYY-MM-DD').add(1, 'year'))

    const selectedBranchSettings = data.branchId ? generalSettings?.branches?.[data.branchId] : undefined;
    const effectiveAllowAppointmentsWithoutRoom = selectedBranchSettings?.allowAppointmentsWithoutRoom ?? generalSettings?.allowAppointmentsWithoutRoom;
    const roomRequired = isAppointmentRoomRequired({allowAppointmentsWithoutRoom: effectiveAllowAppointmentsWithoutRoom});

    const isValid = 
        (!!data.clientId || (servicesSettings[data.type]?.serviceType === ServiceType.GroupAppointment))
        && !!data.professionalId 
        && (!roomRequired || !!data.room)
        && !!data.type 
        && !!data.start 
        && !!data.end 
        && (data.price !== undefined) 
        && (!useBranches || !!data.branchId)
        && !recurringTooLong;

    const getDefaultPrice = (clientId: string | undefined, type: string) => {
        const client = clients.find(c => c.id === clientId);
        const serviceSettings = servicesSettings[type];
        return serviceSettings?.defaultPriceByOrigin[client?.origin ?? '']
            ?? serviceSettings?.defaultPrice
            ?? 0;
    };

    const clientSearchConfig = React.useMemo(() => createClientSearchConfig(clients), [clients]);

    const onClientChange = (name: string, id?: string) => {
        const client = clients.find(c => c.id === id) ?? clients.find(c => c.name === name)
        onChange({
            ...data,
            clientName: name,
            clientFileNumber: client?.clientFileNumber ?? undefined,
            clientId: id ?? client?.id ?? '',
            price: getDefaultPrice(client?.id, data.type),
            packId: undefined,
            packName: undefined,
        });
    }

    const onBranchChange = (displayName: string) => {
        const branchId = Object.entries(generalSettings?.branches ?? {}).find(([_, config]) => config.displayName === displayName)?.[0]
        onChange({
            ...data,
            branchId,
            room: undefined as any,
        })
    }

    const onProfessionalChange = (name: string) => {
        const professional: Professional = professionals?.find(c => c.name === name) || { id: '', name: '' };
        const capabilities = professional.capabilities ?? [];
        const type = capabilities.includes(data.type) ? data.type : (capabilities.length === 1 ? capabilities[0] : '');

        const end = type && data.start
            ? minutesToTimeString(parseTimeStringToMinutes(data.start) + (servicesSettings[type]?.defaultDurationMinutes ?? 60))
            : data.end;

        onChange({
            ...data,
            professionalName: name,
            professionalId: professional.id,
            professionalColor: professional.colorCode,
            type,
            price: getDefaultPrice(data.clientId, type),
            end,
        });
    };

    const onAdditionalProfessionalChange = (index: number, name: string) => {
        const professional: Professional = professionals?.find(c => c.name === name) || { id: '', name: '' };
        if (!data.additionalProfessionals?.find(p => p.id === professional.id)) {
            onChange({
                ...data,
                additionalProfessionals: (data.additionalProfessionals?.length ?? 0) >= index+1
                    ? (data.additionalProfessionals ?? []).map((p, i) => i === index ? professional : p)
                    : [...data.additionalProfessionals ?? [], professional]
            });
        }
    };

    const setProfessionalSlots = (size: number) => {
        onChange({
            ...data,
            additionalProfessionals: data.additionalProfessionals?.filter((_, i) => i < size),
        });
        _setProfessionalSlots(size)
    }

    const onDatesChanged = (dates: TimeDayInterval[]) => {
        if(dates.length > 0) {
            onChange({...data, date: dates[0].date, start: dates[0].start, end: dates[0].end });
            setRepeatDates(dates.slice(1));
        }
    }

    const showRecurringWarningPopup = () => {
        if (!original?.recurring && !data.recurring) return false; // Neither original nor current is recurring
        if (!original?.recurring && data.recurring) return false; // Converting single to recurring - don't show popup, just pass true
        if (original?.recurring && !data.recurring) return true; // Removing recurring from appointment
        if (recurringFields.some(field => (original as any)[field] !== (data as any)[field])) return true;
        return JSON.stringify(original?.recurring) !== JSON.stringify(data.recurring);
    };

    const shouldUpdateEntireGroup = () => {
        // When converting from single to recurring, always update entire group
        if (!original?.recurring && data.recurring) return true;
        // Otherwise, let the modal decide
        return undefined;
    };

    const onSaveClick = () => {
        if(!hasChanged) {
            onChange(data);
        } else {
            // Check if date was changed and we need to collect a reason
            if (isEdit && (data.date !== original?.date || data.clientId !== original?.clientId)) {
                setPendingAction('save');
                setPendingReason(createDefaultAppointmentChangeReason())
                return;
            }

            // Check if we need to show the recurring edit modal
            if (isEdit && showRecurringWarningPopup()) {
                setRecurringActionModal('edit');
                return;
            }

            // Proceed with save
            const updateEntireGroup = shouldUpdateEntireGroup();
            performSave(updateEntireGroup);
        }
    }

    const handleReasonConfirm = () => {
        if (pendingAction !== 'save') return;

        const updateEntireGroup = shouldUpdateEntireGroup();
        performSave(updateEntireGroup);
        setPendingReason(undefined);
        setPendingAction(null);
    }

    const saveIfNeeded = (): Promise<unknown> => {
        if (!hasChanged) return Promise.resolve();
        return performSave(undefined, true) ?? Promise.resolve();
    };

    const performSave = (updateEntireGroup?: boolean, keepOpen?: boolean) => {
        const shouldShowRecurringCreation = !!updateEntireGroup || (type === 'create' && (!!repeatDates.length || !!data.recurring));

        return withRecurringAnimation('creating', shouldShowRecurringCreation, () => {
            if (type === 'create' && repeatDates.length && onCreateMany) {
                // Remove id from all appointments when creating in bulk - backend will assign new IDs
                const { id, ...dataWithoutId } = data;
                const appointmentsToCreate = [
                    { ...dataWithoutId, id: undefined },
                    ...repeatDates.map((repeat: TimeDayInterval) => ({ ...dataWithoutId, id: undefined, date: repeat.date, start: repeat.start, end: repeat.end })),
                ];
                return onCreateMany(
                    appointmentsToCreate as unknown as AppointmentData[], // TODO - create 2 DTOs to avoid this cast, 1 with ID another without
                    ignoreAvailability,
                ).then((res: BulkAppointmentCreationResult | null) => {
                    interface BulkCreateResultItem { id?: string; date: string; start: string; end?: string }

                    const failedDateStart: string[] = (res?.results?.filter((r: BulkCreateResultItem) => !r.id).map((r: BulkCreateResultItem) => r.date + ' ' + r.start)) ?? []
                    if (failedDateStart.length) {
                        console.log(`Failed to create: ${failedDateStart}`)

                        const newRepeatDates: TimeDayInterval[] = repeatDates.filter(d => failedDateStart.includes(d.date + ' ' + d.start))
                        if (newRepeatDates.length && !failedDateStart.includes(data.date + ' ' + data.start)) {
                            const [{date, start, end}] = newRepeatDates.splice(0, 1)
                            onChange({...data, date, start, end})
                        }
                        setRepeatDates(newRepeatDates)
                    }
                    // Reload client packs
                    if(data.clientId && canEditPayments) onLoadClientPacks && onLoadClientPacks(data.clientId)
                    return res;
                })
            } 
            
            return onSave(data, pendingReason, ignoreAvailability, updateEntireGroup, keepOpen).then(() => {
                // Reload client packs
                if (data.clientId && canEditPayments) onLoadClientPacks && onLoadClientPacks(data.clientId)
            })
        })
    }

    const temporaryReadonly = !hasChanged && type === 'edit';

    const expensesOptions: MultipleDataCheckboxesProps<ExtraItem>['options'] = !repeatDates.length && data.type 
        ? expensesAvailable.map(e => ({value: e, label: canSeePrices ? `${e.name} (${e.value.toFixed(2)} €)` : e.name}))
        : []

    const allServices = Object.keys(servicesSettings)

    const professionalsOptions = useBranches 
        ? professionals.filter(p => p.branchTimeAvailability?.[data.branchId ?? selectedBranch ?? 'unknown']?.length)
        : professionals

    const servicesForProfessional = professionalsOptions.find(p => p.id === data.professionalId)?.capabilities ?? []

    const professionalsForService = professionalsOptions.filter(p => p.capabilities?.includes(data.type))
    
    const possibleAdditionalProfessionals = professionalsForService.filter(p => 
        p.id !== data.professionalId && !data.additionalProfessionals?.find(pp => pp.id === p.id) && p.capabilities?.includes(data.type))

    const appointmentTypeDisplayOptions = React.useMemo(() => {
        const baseServiceTypes = data.professionalId ? servicesForProfessional : allServices;
        const packAppointmentTypes = availablePacks.map(option => option.pack.appointmentTypes);
        const categoryByServiceType = Object.fromEntries(
            baseServiceTypes.map(serviceType => [serviceType, servicesSettings[serviceType]?.category]),
        );

        return buildAppointmentTypeDisplayOptions(
            baseServiceTypes,
            packAppointmentTypes,
            categoryByServiceType,
            t(TK.other),
            t(TK.availableInPacks),
        );
    }, [data.professionalId, servicesForProfessional, allServices, availablePacks, servicesSettings, t]);

    const handlePaymentsChange = React.useCallback((values: Payment[]) => {
        if(canDeletePayments) {
            onChange({...data, payments: values})
            return
        }
        else {
            const originalPayments = original?.payments ?? []
            const valuesWithoutOriginal = values.splice(originalPayments.length)
            onChange({...data, payments: originalPayments.concat(valuesWithoutOriginal)})
        }
    }, [data])

    const totalPriceWithoutVAT = React.useMemo(() => getTotalPrice(data), [data])
    const totalPriceWithVAT = React.useMemo(() => totalPriceWithoutVAT * (1 + (data.vat ?? 0)), [totalPriceWithoutVAT, data.vat])
    const totalPaid = React.useMemo(() => getAppointmentTotalPayments(data), [data])

    const totalInvoiced = React.useMemo(() =>
        (data.invoices ?? [])
            .filter(i => i.state !== InvoiceState.Canceled)
            .reduce((sum, i) => {
                if (i.type === InvoiceDocumentType.CreditNote) return sum - (i.total ?? 0);
                if (i.type === InvoiceDocumentType.Receipt) return sum;
                return sum + (i.total ?? 0);
            }, 0),
        [data.invoices]
    );

    const invoicePendingReceipt = React.useMemo(() =>
        (data.invoices ?? []).find(i =>
            i.type === InvoiceDocumentType.Invoice
            && i.state !== InvoiceState.Canceled
            && i.state !== InvoiceState.Draft
            && !('hasReceipt' in i && i.hasReceipt)
            && !('relatedDocuments' in i && (i as Invoice).relatedDocuments?.some(d => d.type === InvoiceDocumentType.Receipt))
        ),
        [data.invoices]
    );

    const autoCancellationWarning = React.useMemo(() => {
        if (type !== 'edit') return null;
        const prePayment = generalSettings.prePayment;
        if (!prePayment?.enabled || !prePayment.autoDeleteIfNotPaid) return null;

        // Find the most specific group for this service type (case-insensitive, matching backend)
        const group = prePayment.autoDeleteGroups?.find(g =>
            g.applicableServices?.some(s => s.toLowerCase() === data.type?.toLowerCase())
        );

        // Group explicitly disables auto-delete for this service
        if (group?.autoDeleteIfNotPaid === false) return null;

        // No group match — apply the global applicableServices filter
        if (!group) {
            const applicable = !prePayment.applicableServices?.length || prePayment.applicableServices.includes(data.type);
            if (!applicable) return null;
        }

        // When a group matches, use only its timing (no fallback to root)
        const daysAfterCreation = group
            ? ((group.autoDeleteDaysAfterCreation ?? 0) > 0 ? group.autoDeleteDaysAfterCreation! : null)
            : ((prePayment.autoDeleteDaysAfterCreation ?? 0) > 0 ? prePayment.autoDeleteDaysAfterCreation! : null);
        const daysBeforeAppointment = group
            ? ((group.autoDeleteDaysBeforeAppointment ?? 0) > 0 ? group.autoDeleteDaysBeforeAppointment! : null)
            : ((prePayment.autoDeleteDaysBeforeAppointment ?? 0) > 0 ? prePayment.autoDeleteDaysBeforeAppointment! : null);
        const dayOfMonth = group
            ? ((group.autoDeleteIfNotPaidOnDayOfMonth ?? 0) >= 1 && (group.autoDeleteIfNotPaidOnDayOfMonth ?? 0) <= 28 ? group.autoDeleteIfNotPaidOnDayOfMonth! : null)
            : ((prePayment.autoDeleteIfNotPaidOnDayOfMonth ?? 0) >= 1 && (prePayment.autoDeleteIfNotPaidOnDayOfMonth ?? 0) <= 28 ? prePayment.autoDeleteIfNotPaidOnDayOfMonth! : null);

        if (daysAfterCreation === null && daysBeforeAppointment === null && dayOfMonth === null) return null;
        if (totalPriceWithVAT - totalPaid <= 0 || !!data.packId) return null;

        const candidates: moment.Moment[] = [];
        if (daysAfterCreation && data.creationDate) candidates.push(moment(data.creationDate).add(daysAfterCreation, 'days'));
        if (daysBeforeAppointment !== null && data.date) candidates.push(moment(data.date).subtract(daysBeforeAppointment, 'days'));
        if (dayOfMonth !== null && data.date) {
            const aptDate = moment(data.date);
            const effectiveDay = Math.min(dayOfMonth, aptDate.daysInMonth());
            const effectiveDate = moment({ year: aptDate.year(), month: aptDate.month(), date: effectiveDay });
            if (effectiveDate.isAfter(moment(data.creationDate))) {
                candidates.push(effectiveDate);
            }
        }
        if (!candidates.length) return null;

        const cancelDate = candidates.reduce((min, d) => d.isBefore(min) ? d : min).format('DD/MM/YYYY');
        return t(TK.paymentMissingAutoCancelOn).replace('{date}', cancelDate);
    }, [type, generalSettings.prePayment, data.packId, data.type, data.date, data.creationDate, totalPriceWithVAT, totalPaid, t])

    const dayCalendarSchedules = React.useMemo<CalendarSchedule[]>(() => {
        return buildAppointmentDayCalendarSchedules({
            calendarDateOnly,
            data,
            dayAppointments,
            professionals,
            professionalColumnName,
            roomColumnName,
            generalSettings,
            selectedBranch,
            userProfessionalId,
            canSeeAppointments,
            labels: {
                absence: t(TK.absence),
                create: t(TK.create),
                roomUnavailable: `${data.room} - ${t(TK.notAvailable)}`,
                unknown: t(TK.unknown),
                busyByProfessional: professionalName => `${t(TK.busy)} ${t(TK.by)} ${professionalName}`,
            },
        });
    }, [calendarDateOnly, data, dayAppointments, professionals, professionalColumnName, roomColumnName, generalSettings, selectedBranch, userProfessionalId, canSeeAppointments, t])

    const wrapperRef = React.useRef<HTMLDivElement>(null)
    const [wrapperWidth, setWrapperWidth] = React.useState(0)
    React.useEffect(() => {
        const el = wrapperRef.current
        if (!el) return
        const observer = new ResizeObserver(([entry]) => setWrapperWidth(entry.contentRect.width))
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    const handleCalendarClick = React.useCallback((_column: unknown, minutesOfDay: number) => {
        const snappedMinutes = minutesOfDay - minutesOfDay % 15;
        const currentDuration = parseTimeStringToMinutes(data.end) - parseTimeStringToMinutes(data.start);
        const duration = currentDuration > 0 ? currentDuration : 60;
        onChange({ ...data, start: minutesToTimeString(snappedMinutes), end: minutesToTimeString(snappedMinutes + duration) });
    }, [data, onChange])
    
    const onTypeChanged = React.useCallback((type: string) => {
        const hasUncanceledInvoices = (data.invoices ?? []).some(invoice => invoice.state !== InvoiceState.Canceled);
        if (type !== data.type && hasUncanceledInvoices) {
            dispatch(alertError(t(TK.cannotChangeAppointmentTypeWithActiveInvoices)));
            return;
        }

        const end = parseTimeStringToMinutes(data.start) + (servicesSettings[type]?.defaultDurationMinutes ?? 60)
        const autoAssignPack = !data.packId
            ? availablePacks.find(option => option.pack.appointmentTypes.includes(type))?.pack
            : undefined

        onChange({
            ...data,
            type,
            price: getDefaultPrice(data.clientId, type),
            vat: (taxes.taxRatesMap[servicesSettings[type]?.invoiceItemDetails?.taxId ?? '']?.rate ?? 0) / 100,
            end: minutesToTimeString(end),
            packId: autoAssignPack?.id,
            packName: autoAssignPack?.packName,
            // Clear professional if they don't have capability for new service type
            ...(!type || professionals.some(p => p.capabilities?.includes(type) && p.id === data.professionalId) ? {} : {
                professionalId: '',
                professionalName: undefined,
                professionalColor: undefined,
                additionalProfessionals: undefined,
            }),
            // Clear client if changing to GroupAppointment
            ...(servicesSettings[type]?.serviceType === ServiceType.GroupAppointment ? {
                clientId: undefined,
                clientName: undefined,
            } : {}),
        })
    }, [data, servicesSettings, professionalsForService, availablePacks])

    const packIsMandatory = useProfessionalPacks && isProfessional

    const professionalCanEditAppointment = React.useMemo(() =>
        isProfessional && (isCreate || checkProfessionalCanEditAppointment(data, userProfessionalId, generalSettings)),
        [data.id])

    const canEditAppointment = (canManageAppointments || professionalCanEditAppointment) && canCreateOrEditAppointments
    
    const onInvoiceCreated = React.useCallback((invoice: InvoiceReduced) => {
        handleInvoiceCreatedWithSync(invoice, {
            getLatestData: () => latestDataRef.current,
            onChange,
            setInvoiceModal,
            getAppointment: getAppointmentAsync,
            onSyncError: (error) => {
                console.error('Error syncing appointment after invoice emission:', error)
            },
        })
    }, [onChange])

    const onReceiptEmitted = React.useCallback(() => {
        handleReceiptEmittedWithSync({
            getLatestData: () => latestDataRef.current,
            onChange,
            getAppointment: getAppointmentAsync,
            onSyncError: (error) => {
                console.error('Error syncing appointment after receipt emission:', error)
            },
        })
    }, [onChange])

    const confirmClientRequest = () => {
        dispatch(updateEntity(
            'appointments', 
            {...data, clientRequest: ClientRequestType.Approved}, 
            () => confirmClientAppointment(data.id),
            true,
        ))
    }

    const handleOpenConfirmationMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setConfirmationMenuAnchor(event.currentTarget);
    };

    const handleCloseConfirmationMenu = () => setConfirmationMenuAnchor(null);

    const handleConfirmationStatusChange = async (status: AppointmentConfirmationEditableStatus) => {
        const updated = applyAppointmentConfirmationStatus(data, status);
        handleCloseConfirmationMenu();
        try {
            await updateAppointmentConfirmationStatusAsync(data.id, updated.confirmedByClientAt, updated.deniedByClientAt);
            onChange(updated);
        } catch (error) {
            dispatch(alertError(getErrorMessage(error) || t(TK.somethingWentWrong)));
        }
    };

    const allRoomsOptions = selectedBranch
        ? [...roomsSet, ...Object.keys(generalSettings.branches?.[data.branchId ?? selectedBranch]?.rooms ?? {})]
        : roomsSet

    const serviceDefaultRooms: string[] = servicesSettings[data.type]?.defaultRooms ?? [];
    const roomsOptions = (isAdmin || canManageAppointments || serviceDefaultRooms.length === 0)
        ? allRoomsOptions
        : allRoomsOptions.filter(room => serviceDefaultRooms.includes(room));

    const hasConfirmationRequestSent = (data.confirmationRequestsSentBySms?.length ?? 0) > 0
        || (data.confirmationRequestsSentByWhatsapp?.length ?? 0) > 0;
    const hasClientResponded = !!data.confirmedByClientAt || !!data.deniedByClientAt;
    const confirmationStatus = confirmationFeatureAvailable && (hasClientResponded || (isFutureAppointment(data) && hasConfirmationRequestSent))
        ? getConfirmationStatus(data)
        : null;
    const confirmationDate = confirmationStatus === 'confirmed'
        ? data.confirmedByClientAt
        : confirmationStatus === 'rejected'
            ? data.deniedByClientAt
            : getConfirmationRequestSentAt(data);
    const confirmationDateLabel = formatDateTime(confirmationDate);
    const confirmationTitleBase = confirmationStatus === 'confirmed'
        ? t(TK.clientConfirmedAppointment)
        : confirmationStatus === 'rejected'
            ? t(TK.clientRejectedAppointment)
            : confirmationStatus === 'pending'
                ? t(TK.clientAwaitingConfirmation)
                : '';
    const confirmationTitle = confirmationDateLabel
        ? `${confirmationTitleBase} (${t(TK.date)}: ${confirmationDateLabel})`
        : confirmationTitleBase;

    return (
        <div ref={wrapperRef} style={{display: 'flex', gap: 'var(--spacer-M)'}}>
        {wrapperWidth >= 1000 && calendarDateOnly && (!!data.professionalId || !!data.room) && (
            <CustomDayCalendar
                className='appointmintFormCalendar'
                columns={[
                    ...(data.professionalId ? [{name: professionalColumnName, value: professionalColumnName}] : []),
                    ...(data.room ? [{name: roomColumnName, value: roomColumnName}] : []),
                ]}
                schedules={dayCalendarSchedules}
                openingHourInMinutesOfDay={parseTimeStringToMinutes(generalSettings.openningHour)}
                closingHourInMinutesOfDay={parseTimeStringToMinutes(generalSettings.closingHour)}
                stepInMinutes={60}
                layout={{columnsWidth: 120}}
                onClick={handleCalendarClick}
                mouseLabel={m => minutesToTimeString(Math.floor(m / 15) * 15)}
            />
        )}
        <Form className="appointmentForm" style={{display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)', flex: 1, minWidth: 0}}>

            {recurringOperation && (
                <div className="appointmentForm__overlay">
                    <Spinner color="primary" style={{width: '3rem', height: '3rem'}} />
                    <Typography size='large'><b>{t(recurringOperation === 'creating' ? TK.creatingAppointment : TK.deletingAppointment)}</b></Typography>
                </div>
            )}

            { data.clientRequest === ClientRequestType.Approved && <div style={{textAlign: 'end'}}><b>🧑 {t(TK.createdByTheClient)}</b></div> }
            { data.clientRequest === ClientRequestType.Cancel && <div style={{textAlign: 'end'}}><b>🙅‍♂️ {t(TK.cancelRequestedByTheClient)}</b></div> }
            { data.clientRequest === ClientRequestType.Reschedule && <div style={{textAlign: 'end'}}><b>🙋‍♂️ {t(TK.rescheduleRequestedByTheClient)}</b></div> }
            { data.clientRequest === ClientRequestType.Create && 
                <div style={{display: 'flex', flexDirection: 'row-reverse'}}>
                    { canManageAppointments && (
                        <div style={{display: 'flex', alignItems: 'center', gap: 'var(--spacer-XXS)'}}>
                            <AppButton color='link' style={{paddingTop: 0}} disabled={isSubmitting} onClick={confirmClientRequest}>{t(TK.clickHereToConfirmAndNotifyTheClient)}.</AppButton>
                            <Tooltip title={t(TK.confirmAndNotifyClientTooltip)} placement="top">
                                <InfoOutlinedIcon style={{fontSize: 16, opacity: 0.65}} />
                            </Tooltip>
                        </div>
                    ) }
                    <b>🙋‍♂️ {t(TK.createdByTheClient)}.</b>
                </div> }

            {confirmationStatus ? (
                <>
                    <AppointmentConfirmation>
                        <ConfirmationIndicator $status={confirmationStatus} title={confirmationTitle}>
                            {confirmationStatus === 'confirmed' && <CheckCircleOutlineIcon fontSize="small" />}
                            {confirmationStatus === 'rejected' && <CancelOutlinedIcon fontSize="small" />}
                            {confirmationStatus === 'pending' && <HourglassEmptyIcon fontSize="small" />}
                        </ConfirmationIndicator>
                        <Typography size="small">{confirmationTitle}</Typography>
                        {canConfirmAppointments && (
                            <IconButton
                                id="appointment-confirmation-edit-button"
                                size="small"
                                type="button"
                                onClick={handleOpenConfirmationMenu}
                                style={{ padding: 0, marginLeft: 'var(--spacer-XXS)', cursor: 'pointer' }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        )}
                    </AppointmentConfirmation>
                    <AppointmentConfirmationStatusMenu
                        anchorEl={confirmationMenuAnchor}
                        open={!!confirmationMenuAnchor}
                        onClose={handleCloseConfirmationMenu}
                        onSelect={status => { void handleConfirmationStatusChange(status); }}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    />
                </>
            ) : null}

            <AutocompleteTextField
                id="appointment-type"
                inputId="appointment-type-value"
                label={t(TK.appointmentType)}
                value={data.type}
                disabled={!canEditAppointment || temporaryReadonly}
                options={appointmentTypeDisplayOptions.orderedServiceTypes}
                highlightedOptions={appointmentTypeDisplayOptions.highlightedServiceTypes}
                groupBy={appointmentTypeDisplayOptions.groupByServiceType
                    ? option => appointmentTypeDisplayOptions.groupByServiceType?.[option] ?? t(TK.other)
                    : undefined}
                optionLabelResolver={serviceType => serviceName(serviceType, generalSettings)}
                searchTextResolver={serviceType => serviceName(serviceType, generalSettings)}
                onValueChanged={onTypeChanged}
            />

            <div style={{display: 'flex'}}>
                <AutocompleteTextField
                    id="appointment-professional-name"
                    inputId="appointment-professional-name-value"
                    style={{flexGrow: 1}}
                    label={<span>
                            {t(TK.professional)} {data.professionalId && <ColorBall size="xs" style={{marginRight: 'var(--spacer-XS)'}} color={data.professionalColor}/>}
                        </span>}
                    value={data.professionalName || ''}
                    options={(data.professionalName || !data.type ? professionalsOptions : professionalsForService).map(c => c.name)}
                    disabled={!canManageAppointments || temporaryReadonly}
                    onValueChanged={proffesionalName => onProfessionalChange(proffesionalName)} 
                />
                {canManageAppointments && !temporaryReadonly && <AppButton className="entityLinkButton" color="link" onClick={() => setProfessionalSlots(professionalSlots+1)}>
                    <AddIcon/>
                </AppButton>}
                { onOpenProfessional &&
                    <AppButton className="entityLinkButton" color="link" onClick={() => onOpenProfessional(data.professionalId)}>
                        <LaunchIcon/>
                    </AppButton>
                }
            </div>

            {[...new Array(professionalSlots)].map((_ ,i) =>
                <div key={i} style={{display: 'flex'}}>
                    <AutocompleteTextField
                        id={`appointment-professional-additional-${i}`}
                        style={{flexGrow: 1}}
                        value={data.additionalProfessionals?.[i]?.name ?? ''}
                        label={<span>
                            {t(TK.professional)} <ColorBall size="xs" style={{marginRight: 'var(--spacer-XS)'}} color={data.additionalProfessionals?.[i]?.colorCode ?? ''}/>
                        </span>}
                        options={(data.type ? possibleAdditionalProfessionals : professionals).map(c => c.name)}
                        disabled={!canManageAppointments || temporaryReadonly}
                        onValueChanged={proffesionalName => onAdditionalProfessionalChange(i, proffesionalName)} 
                    />
                    { canManageAppointments && !temporaryReadonly &&
                        <AppButton className="entityLinkButton" color="link" onClick={() => setProfessionalSlots(professionalSlots-1)}>
                            <RemoveIcon/>
                        </AppButton>
                    }
                    { onOpenProfessional && data.additionalProfessionals && data.additionalProfessionals?.[i] &&
                        <AppButton className="entityLinkButton" color="link" onClick={() => data.additionalProfessionals?.[i]?.id && onOpenProfessional(data.additionalProfessionals[i].id)}>
                            <LaunchIcon/>
                        </AppButton>
                    }
                </div>
            )}

            { servicesSettings[data.type]?.serviceType !== ServiceType.GroupAppointment &&
                    <div style={{display: 'flex'}}>
                        <AutocompleteTextField
                            id="appointment-client-name"
                            inputId="appointment-client-name-value"
                            style={{flexGrow: 1}}
                            value={clientSearchConfig.getOptionByClientId(data.clientId) ?? data.clientName ?? ''}
                            label={t(TK.client)}
                            options={clientSearchConfig.options}
                            optionLabelResolver={clientSearchConfig.optionLabelResolver}
                            searchTextResolver={clientSearchConfig.searchTextResolver}
                            searchMatcher={clientSearchConfig.searchMatcher}
                            disabled={!canEditAppointment || temporaryReadonly}
                            onAddNewOption={onCreateNewClient && (canManageClients || professionalCanEditOwnClient) ? (newClientName => onCreateNewClient(newClientName, userProfessionalId).then((id) => onClientChange(newClientName, id))) : undefined}
                            onValueChanged={option => {
                                const client = clientSearchConfig.getClientByOption(option);
                                onClientChange(client?.name ?? option, client?.id);
                            }} />
                        { !isEdit && waitingItems.length > 0 && <>
                            <Tooltip title={`${waitingItems.length} ${t(TK.waitingList).toLowerCase()}`}>
                                <IconButton style={{color: 'var(--warning-color)'}} onClick={e => setWaitingMenuAnchor(e.currentTarget)}>
                                    <WarningIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                            <Menu
                                anchorEl={waitingMenuAnchor}
                                open={!!waitingMenuAnchor}
                                onClose={() => setWaitingMenuAnchor(null)}
                            >
                                <div style={{
                                    padding: 'var(--spacer-XS) var(--spacer-S)',
                                    borderBottom: 'var(--border-thin) solid var(--appLigthGrey)',
                                    marginBottom: 'var(--spacer-XS)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--spacer-XS)',
                                    color: 'var(--warning-color)',
                                }}>
                                    <WarningIcon style={{fontSize: '1rem'}}/>
                                    <span style={{fontWeight: 'bold', fontSize: '0.85rem', color: 'var(--text-body)'}}>
                                        {t(TK.waitingList)} ({waitingItems.length})
                                    </span>
                                </div>
                                {waitingItems.map((item, idx) => (
                                    <Tooltip key={idx} title={item.notes ?? ''} placement="left" disableHoverListener={!item.notes}>
                                        <MenuItem onClick={() => {
                                            onClientChange(item.clientName ?? '', item.clientId);
                                            setWaitingMenuAnchor(null);
                                        }}>
                                            <span style={{fontWeight: 500}}>{item.clientName}</span>
                                            {item.addedAt && <span style={{marginLeft: 'var(--spacer-XS)', color: 'var(--text-muted)', fontSize: '0.8em'}}>({item.addedAt.substring(0, 10)})</span>}
                                        </MenuItem>
                                    </Tooltip>
                                ))}
                            </Menu>
                        </>}

                        {(canSendSurveyFromAppointment || (isEdit && onOpenClient)) && (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                {canSendSurveyFromAppointment && (() => {
                                    const lastSent = data.surveyHistory?.[data.surveyHistory.length - 1];
                                    const surveySent = !!lastSent;
                                    const tooltipContent = lastSent
                                        ? `${t(TK.surveySentAt)}: ${formatDateTime(lastSent.sentAt)}`
                                        : sendSurveyAction.canSendSurvey ? t(TK.sendSurvey) : (sendSurveyAction.sendSurveyDisabledMessage ?? t(TK.clientContactRequiredForSurvey));
                                    const surveyButtonColor = surveySent
                                        ? 'var(--success-color)'
                                        : 'var(--text-muted)';
                                    return (
                                        <>
                                            <AppButton
                                                className="entityLinkButton"
                                                color="link"
                                                id="appointment-send-survey-button"
                                                style={{ color: surveyButtonColor }}
                                                onClick={() => {
                                                    if (!sendSurveyAction.canSendSurvey) return;
                                                    sendSurveyAction.openSendSurveyModal();
                                                }}
                                                disabled={!sendSurveyAction.canSendSurvey}
                                            >
                                                <AssignmentIcon />
                                            </AppButton>
                                            <ToolTip
                                                target='appointment-send-survey-button'
                                                toolTipContent={tooltipContent}
                                            />
                                        </>
                                    );
                                })()}
                                {isEdit && onOpenClient && (
                                    <AppButton className="entityLinkButton" color="link" onClick={() => data.clientId && onOpenClient(data.clientId)}>
                                        <LaunchIcon/>
                                    </AppButton>
                                )}
                            </div>
                        )}
                    </div>
                }
                {(() => {
                    const warningNote = data.clientId ? clients.find(c => c.id === data.clientId)?.warningNote : undefined;
                    return warningNote
                        ? <div style={{ color: 'var(--danger-color)', fontSize: '0.75em', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '-1.5rem' }}>
                            <WarningIcon style={{ fontSize: '1em' }} />{warningNote}
                          </div>
                        : null;
                })()}

                {useBranches &&
                    <AutocompleteTextField
                        label={t(TK.branch)}
                        value={generalSettings?.branches?.[data.branchId ?? 0]?.displayName}
                        disableClearable
                        disabled={!canEditAppointment || temporaryReadonly}
                        options={Object.values(generalSettings?.branches ?? {}).map(b => b.displayName)}
                        onValueChanged={onBranchChange}
                        autoComplete="off"
                    />
                }

                <div style={{display: 'flex', gap: 'var(--spacer-S)'}}>
                    <div style={{flexGrow: 1}}>
                        <AutocompleteTextField
                            id="appointment-room"
                            inputId="appointment-room-value"
                            label={roomRequired ? t(TK.room) : `${t(TK.room)} (${t(TK.optional)})`}
                            value={data.room || ''}
                            options={roomsOptions}
                            disabled={!canEditAppointment || temporaryReadonly}
                            onValueChanged={room => onChange({...data, room})}
                        />
                    </div>

                    <div style={{flexGrow: 1, ...hide(!canSeePrices)}}>
                        <NumberInputField
                            id="appointment-price"
                            label={t(TK.price)}
                            prepend="€"
                            min={0}
                            value={data.price}
                            disabled={!canSeePrices || temporaryReadonly}
                            onChange={price => onChange({...data, price: price as any})}
                        />
                    </div>
                </div>
                
                <TextInputField
                    id="appointment-notes"
                    label={t(TK.notes)}
                    type="textarea"
                    value={data.notes || ''}
                    disabled={!canEditAppointment || temporaryReadonly}
                    onChange={notes => onChange({...data, notes})}
                />

                <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)'}}>
                    <TimeDayIntervalField
                        id="appointment-time-intervals"
                        mandatory
                        allowAdd={!isEdit && !recurring}
                        values={[data, ...repeatDates]}
                        disabled={!canEditAppointment || temporaryReadonly}
                        onChange={dates => onDatesChanged(dates)}
                        onSelected={interval => setCalendarDate(interval.date)}
                    />
                    {recurring &&
                        <RecurringEventField
                            id="appointment-recurring-intervals"
                            value={data.recurring ?? {endDate: moment(data.date).add(1, 'month').format("YYYY-MM-DD"), frequency: RecurrenceFrequency.Weekly, weekDays: [moment(data.date).day()], period: 1} }
                            disabled={!canEditAppointment || temporaryReadonly}
                            onChange={recurring => onChange({...data, recurring})}
                        />
                    }
                    {recurring && (
                        <CheckInputField
                            label={t(TK.ignoreAvailability)}
                            helpText={t(TK.ignoreAvailabilityRecurringHelp)}
                            value={ignoreAvailability}
                            onChange={setIgnoreOverlaps}
                        />
                    )}
                    {repeatDates.length === 0 && canEditAppointment && !temporaryReadonly &&
                        <div style={{textAlign: 'end'}}>
                            <Typography
                                className='mouseHoverUnderline'
                                onClick={() => {
                                    setRecurring(!recurring);
                                    if (recurring) {
                                        // Removing recurring
                                        onDatesChanged([data]);
                                    } else {
                                        // Adding recurring - set default values
                                        onChange({
                                            ...data,
                                            recurring: {
                                                endDate: moment(data.date).add(1, 'month').format("YYYY-MM-DD"),
                                                period: 1,
                                                frequency: RecurrenceFrequency.Weekly,
                                                weekDays: [moment(data.date).day()]
                                            }
                                        });
                                    }
                                }}
                                size='smaller' style={{color: 'grey', cursor: 'pointer'}}
                            >
                                <i>{recurring ? t(TK.notRecurring) : t(TK.makeItRecurring)}</i>
                            </Typography>
                            {recurringTooLong && <FormHelperText error>{t(TK.recurringLimitExceeded)}</FormHelperText>}
                        </div>
                    }
                </div>

                { servicesSettings[data.type]?.serviceType === ServiceType.GroupAppointment && (
                    <GroupClientsSection
                        clients={clients}
                        groupClients={data.groupClients ?? []}
                        appointmentType={data.type}
                        disabled={!canEditAppointment || temporaryReadonly}
                        onOpenClient={onOpenClient}
                        onChange={groupClients => onChange({...data, groupClients})}
                    />
                )}

                <hr style={{width: '100%', flexGrow: 1}}/>

                {(!!data.expenses?.length || !!expensesOptions.length) && (
                    <Row>
                        <Col sm="12">
                            <ExtrasList
                                id="appointment"
                                expenses={data.expenses ?? []}
                                expensesOptions={expensesOptions}
                                readOnly={temporaryReadonly || !canEditAppointment}
                                canSeePrices={canSeePrices}
                                onAdd={(expense) => onChange({...data, expenses: [...(data.expenses ?? []), expense]})}
                                onDelete={(index) => onChange({...data, expenses: data.expenses?.filter((_, i) => i !== index)})}
                                onUpdate={(index, expense) => onChange({...data, expenses: data.expenses?.map((e, i) => i === index ? expense : e)})}
                                openAddNewPopup={openExtrasPopup}
                                onOpenAddNewPopupChange={setOpenExtrasPopup}
                            />
                        </Col>
                    </Row>
                )}

                { (canEditPayments || packIsMandatory) && (data.packId || !!(data.payments?.length ?? 0)) && (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <label style={{ fontSize: '0.875rem', fontWeight: 500, marginRight: '4px' }}>{t(TK.payment)}</label>
                        </div>
                        <div>
                            {data.packId && (
                                <div style={{display: 'flex', alignItems: 'stretch', gap: 'var(--spacer-XS)', marginBottom: 'var(--spacer-S)'}}>
                                    {(canDeletePayments || packIsMandatory) && (
                                        <AppIconButton
                                            onClick={() => onChange({...data, packId: undefined, packName: undefined})}
                                        >
                                            <Delete fontSize="small" color="error" />
                                        </AppIconButton>
                                    )}
                                    <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 'var(--spacer-XS)', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                        <Typography size='small'>
                                            <b>{packData?.isSubscription ? t(TK.subscription) : t(TK.pack)}:</b>{' '}
                                            {getPackDesc(data.clientName, packData)}
                                            {data.packAppointmentPosition != null && data.packTotalCredits ? ` (${data.packAppointmentPosition}/${data.packTotalCredits})` : undefined}
                                            {data.packAppointmentPosition != null && data.packTotalMinutes ? ` (${data.packAppointmentPosition}/${minutesToTimeString(data.packTotalMinutes)})` : undefined}
                                        </Typography>
                                    </div>
                                </div>
                            )}

                            {data.payments && data.payments.length > 0 && canEditPayments && (
                                <PaymentsList
                                    values={data.payments}
                                    canDelete={canDeletePayments}
                                    onDelete={(_, index) => onChange({...data, payments: data.payments?.filter((_, i) => i !== index)})}
                                    missingToPay={totalPriceWithVAT - totalPaid}
                                />
                            )}
                        
                            {isAdmin && packData && packSettings && (
                                <Typography size='smaller' block style={{textAlign: 'end', color: 'var(--text-muted)'}}>
                                    {t(TK.noteAboutRealPriceWithPack)} <b>{formatEuros(getActualPriceWithPack(data, packData, packSettings))}</b>
                                </Typography>
                            )}
                        </div>
                    </div>
                )}

                { seeInvoices && (!!data.invoices?.length || invoiceModal) && data.clientId &&
                    <div>
                        <label style={{ fontSize: '0.875rem', marginBottom: '8px', display: 'block', fontWeight: 500 }}>{t(TK.invoices)}</label>
                        <InvoicesTable
                            clientId={data.clientId}
                            appointment={data}
                            createNew={invoiceModal}
                            emitReceiptForInvoiceId={emitReceiptForInvoiceId}
                            invoices={data.invoices ?? []}
                            hideCreateButton={true}
                            onUpdate={invoice => onChange({...data, invoices: data.invoices?.map(i => i.id === invoice.id ? invoice : i) as Invoice[]})}
                            onCreated={onInvoiceCreated}
                            onReceiptEmitted={onReceiptEmitted}
                            onModalClose={() => { setInvoiceModal(false); setEmitReceiptForInvoiceId(undefined); }}
                        />
                    </div>
                }

                {canSeePrices && <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                    {totalPriceWithVAT === totalPriceWithoutVAT ? (
                        <Typography id="appointment-total-price-label" size='x-large' style={{whiteSpace: 'nowrap'}}><b>{totalPriceWithoutVAT?.toFixed(2) ?? '-'} €</b></Typography>
                    ) : (
                        <>
                            <Typography id="appointment-total-price-label" size='x-large' style={{whiteSpace: 'nowrap'}}><b>{totalPriceWithVAT?.toFixed(2) ?? '-'} €</b></Typography>
                            <Typography id="appointment-total-price-without-vat-label" size='small' style={{ color: 'var(--text-muted)', marginTop: 'var(--spacer-XXS)', whiteSpace: 'nowrap' }}>
                                {t(TK.withoutVAT)}: {totalPriceWithoutVAT?.toFixed(2) ?? '-'} €
                            </Typography>
                        </>
                    )}
                    {(!data.payments || data.payments.length === 0) && !data.packId && totalPriceWithVAT > 0 && (
                        <span style={{color: 'var(--danger-color)', fontSize: '0.75rem'}}>
                            {t(TK.missingPayment)}
                        </span>
                    )}
                </div>}

                {isEdit && servicesSettings[data.type]?.serviceType !== ServiceType.GroupAppointment &&

                    <div style={{display: 'flex', flexDirection: 'row', gap: 'var(--spacer-XS)', flexWrap: 'wrap'}}>
                        { (canEditPayments || useProfessionalPacks) &&
                            <PaymentsButtons
                                entity={data}
                                totalToPay={totalPriceWithVAT}
                                totalPaid={totalPaid}
                                showManual
                                showAutomatic={isEdit}
                                showPacks
                                availablePacks={availablePacks}
                                openPaymentModal={openPaymentModal}
                                onPaymentAdded={payment => onChange({...data, payments: [...data.payments??[], payment]})}
                                onPackAdded={pack => onChange({...data, packId: pack?.id, packName: pack?.packName})}
                                onPaymentsChanged={handlePaymentsChange}
                            />
                        }
                        { !generalSettings.doNotUseAppointmentConfirmation && canConfirmAppointments && data.date <= moment().format('YYYY-MM-DD') && (
                            <AppButton
                                size="lg"
                                style={{width: '14rem'}}
                                outline={data.hasHappened === undefined}
                                color={data.hasHappened === true ? 'success' : data.hasHappened === false ? 'danger' : 'secondary'}
                                onClick={() => onChange({...data, hasHappened: data.hasHappened === undefined ? true : data.hasHappened === true ? false : undefined})}
                            >
                                {data.hasHappened === false
                                    ? <CancelOutlinedIcon fontSize="small"/>
                                    : <CheckCircleOutlineIcon fontSize="small"/>
                                }
                                {data.hasHappened === true ? t(TK.confirmed) : data.hasHappened === false ? t(TK.appointmentNotHappened) : t(TK.confirm)}
                            </AppButton>
                        )}
                        { canEmitInvoices && !data.packId && invoicePendingReceipt && (
                            <AppButton
                                size="lg"
                                color="primary"
                                style={{width: '14rem'}}
                                outline
                                onClick={() => { void saveIfNeeded().then(() => setEmitReceiptForInvoiceId(invoicePendingReceipt.id)); }}
                            >
                                <ReceiptIcon fontSize="small"/>
                                {t(TK.emitReceipt)}
                            </AppButton>
                        )}
                        { canEmitInvoices && !data.packId && !invoicePendingReceipt && Math.abs(totalInvoiced - totalPriceWithVAT) > 0.01 && (
                            <AppButton
                                size="lg"
                                color="primary"
                                style={{width: '14rem'}}
                                outline
                                onClick={() => { void saveIfNeeded().then(() => setInvoiceModal(true)); }}
                            >
                                <ReceiptIcon fontSize="small"/>
                                {t(TK.issueInvoice)}
                            </AppButton>
                        )}
                    </div>
                }

            {!canCreateOrEditAppointments && (
                <PaymentWarningBanner>
                    <InfoOutlinedIcon style={{color: 'var(--info-color)', flexShrink: 0}} fontSize="small" />
                    <Typography size="small">{t(TK.noPermissionToCreateOrEditAppointmentsContactAdmin)}</Typography>
                </PaymentWarningBanner>
            )}

            <div className="buttonsContainer" >
                { canEditAppointment && <>
                    { hasChanged && isValid && canSkipAvailability && !recurring && <span className="ignoreAvailabilityCheckbox">
                        <Checkbox id="appointment-ignore-availability-checkbox" checked={ignoreAvailability} onChange={() => setIgnoreOverlaps(!ignoreAvailability)} />
                        <Typography size='small'>{t(TK.ignoreAvailability)}</Typography>
                    </span> }
                    { !!autoCancellationWarning && <span className="ignoreAvailabilityCheckbox">
                        <Checkbox id="appointment-disable-auto-cancellation-checkbox" checked={!!data.disableAutoCancellation} onChange={() => onChange({ ...data, disableAutoCancellation: !data.disableAutoCancellation })} />
                        <Typography size='small'>{t(TK.disableAutoCancellation)}</Typography>
                    </span> }
                    { isEdit && canEditAppointment && onDelete && <AppButton id="appointment-delete-button" outline color="danger" onClick={() => {
                        if (!hasChanged || hasAppointmentDeletionBlockers(data)) { appointmentDeletion.startDeletion(data); return; }
                        void (performSave(undefined, true) ?? Promise.resolve())
                            .then(() => appointmentDeletion.startDeletion(data));
                    }}><Delete /></AppButton>}
                    <AppButton id="appointment-cancel-button" onClick={onCancel}>{t(TK.cancel)}</AppButton>
                    <AppButton
                        id="appointment-save-button"
                        color="success"
                        disabled={hasChanged && (!isValid || isSubmitting)}
                        onClick={() => onSaveClick()}
                    >
                        {isSubmitting && <Spinner size="sm" color="secundary"/>}
                        {t(isCreate ? TK.create : hasChanged ? TK.save : TK.edit)}
                    </AppButton>
                    { !!changeLog?.length && <ChangeLogInfo changeLog={changeLog}/> }
                </> }
                { !canEditAppointment && (<AppButton id="appointment-close-button" onClick={onCancel}>{t(TK.close)}</AppButton>) }
            </div>

            <Modal isOpen={!!recurringActionModal} toggle={() => setRecurringActionModal(null)}>
                <ModalHeader toggle={() => setRecurringActionModal(null)}>{t(TK.recurring)}</ModalHeader>
                <ModalBody>{t(TK.recurringEditQuestion)}</ModalBody>
                <ModalFooter>
                    <AppButton
                        id="appointment-recurring-this-only-button"
                        color="success"
                        onClick={() => {
                            setRecurringActionModal(null);
                            performSave(false);
                        }}
                    >
                        {t(TK.thisEventOnly)}
                    </AppButton>{' '}
                    <AppButton
                        id="appointment-recurring-following-button"
                        color="success"
                        onClick={() => {
                            setRecurringActionModal(null);
                            performSave(true);
                        }}
                    >
                        {t(TK.thisAndFollowingEvents)}
                    </AppButton>{' '}
                    <AppButton id="appointment-recurring-cancel-button" color="secondary" onClick={() => setRecurringActionModal(null)}>{t(TK.cancel)}</AppButton>
                </ModalFooter>
            </Modal>

            <Modal isOpen={!!pendingReason} toggle={() => setPendingReason(undefined)}>
                <ModalHeader toggle={() => setPendingReason(undefined)}>{t(TK.reasonForChange)}</ModalHeader>
                <ModalBody>
                    <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)'}}>
                        <Typography>{t(TK.pleaseProvideAReason)}</Typography>

                        <AutocompleteTextField
                            id="reason-origin-select"
                            label={t(TK.origin)}
                            value={pendingReason && reasonOriginMap[pendingReason.reasonOrigin] as string || t(TK.other)}
                            options={[
                                t(TK.professional),
                                t(TK.client),
                                t(TK.organization),
                                t(TK.other)
                            ]}
                            onValueChanged={(value) => pendingReason && setPendingReason({
                                ...pendingReason,
                                reasonOrigin: reasonOriginMap[value] as AppointmentChangeOrigin
                            })}
                        />

                        <AutocompleteTextField
                            id="reason"
                            label={t(TK.reason)}
                            value={pendingReason?.reason || ''}
                            options={generalSettings?.appointmentChangeReasons || []}
                            onValueChanged={reason => pendingReason && setPendingReason({...pendingReason, reason})}
                            onAddNewOption={newReason => dispatch(updateGeneralSettings({
                                ...generalSettings,
                                appointmentChangeReasons: [...(generalSettings.appointmentChangeReasons || []), newReason],
                            }))}
                        />

                        <TextInputField
                            id="reason-details"
                            label={t(TK.reasonDetails)}
                            type="textarea"
                            value={pendingReason?.reasonDetails ?? ''}
                            onChange={reasonDetails => pendingReason && setPendingReason({...pendingReason, reasonDetails})}
                        />
                    </div>
                </ModalBody>
                <ModalFooter>
                    <AppButton
                        id="reason-confirm-button"
                        color="success"
                        disabled={
                            !pendingReason
                            || pendingReason.reasonOrigin == null
                            || (
                                !pendingReason.reason?.trim()?.length
                                && !pendingReason.reasonDetails?.trim()?.length
                            )
                        }
                        onClick={handleReasonConfirm}
                    >
                        {t(TK.continueButton)}
                    </AppButton>{' '}
                    <AppButton
                        id="reason-cancel-button"
                        color="secondary"
                        onClick={() => {
                            setPendingReason(undefined);
                            setPendingAction(null);
                        }}
                    >
                        {t(TK.cancel)}
                    </AppButton>
                </ModalFooter>
            </Modal>

            <SendSurveyModal
                isOpen={sendSurveyAction.showSendSurveyModal}
                onClose={sendSurveyAction.closeSendSurveyModal}
                onCreateForm={sendSurveyAction.redirectToCreateForm}
                onSelectedSurveyIdChanged={sendSurveyAction.setSelectedSurveyId}
                onSend={sendSurveyAction.sendSurvey}
                selectedSurveyDisabledMessage={sendSurveyAction.selectedSurveyDisabledMessage}
                selectedSurveyId={sendSurveyAction.selectedSurveyId}
                sendingSurvey={sendSurveyAction.sendingSurvey}
                surveys={sendSurveyAction.surveys}
            />
            {appointmentDeletion.dialogs}

            {autoCancellationWarning && data.disableAutoCancellation !== true && (
                <PaymentWarningBanner>
                    <WarningIcon style={{color: 'var(--warning-color)', flexShrink: 0}} fontSize="small" />
                    <Typography size="small">{autoCancellationWarning}</Typography>
                </PaymentWarningBanner>
            )}
        </Form>
        </div>
    );
}

export default AppointmentForm;
